import type { CollectionProduct } from 'api/shopifyV2/types';
import { Connection } from 'api/shopifyV2/types';
import { StoreCode } from 'schema/storeCode';
import { SanityKeyed, SanityBlock } from 'types/studio/studio';

export enum ShopUrlQueryKey {
  filter = 'filter'
}

export type Image = {
  id: number;
  position: number;
  product_id: number;
  src: string;
  updated_at: string;
  variant_ids: Array<number>;
};

export type ShopUrlQuery = {
  [ShopUrlQueryKey.filter]: keyof typeof ShopFilter;
};

export type ShopifyMetafield =
  | 'shop_all_products'
  | 'shop_gifts'
  | 'shop_supplements'
  | 'shop_accessories'
  | 'shop_gear'
  | 'shop_subscriptions';

export type ShopifyFilter =
  | 'AllProducts'
  | 'Subscriptions'
  | 'Supplements'
  | 'Accessories'
  | 'Gifts'
  | 'Gear';

export enum ShopMetafield {
  shop_all_products = 'shop_all_products',
  shop_gifts = 'shop_gifts',
  shop_supplements = 'shop_supplements',
  shop_accessories = 'shop_accessories',
  shop_gear = 'shop_gear',
  shop_subscriptions = 'shop_subscriptions'
}

export enum ShopFilter {
  AllProducts = 'AllProducts',
  Subscriptions = 'Subscriptions',
  Supplements = 'Supplements',
  Accessories = 'Accessories',
  Gifts = 'Gifts',
  Gear = 'Gear'
}

export enum ShopDescriptors {
  subtitle = 'subtitle'
}

export type OneTimeTranslations = {
  oneTime?: { scoopAvailabilityInfo?: string };
};

export type ShopCmsProductVariant = {
  id: Record<StoreCode, number>;
  title: string;
  description: string;
  variantTitle: string;
  variantDescription: string;
  image: string;
  hoverImage?: string;
  label?: string;
};

export type ShopCmsProduct = {
  id: number | string;
  subscription?: ShopCmsProductVariant;
  subscriptionDouble?: ShopCmsProductVariant;
  oneTime?: ShopCmsProductVariant;
  categories: ShopFilter[];
};

export type ShopCmsData = {
  products: ShopCmsProduct[];
  buttonSubscribe: string;
  buttonAddToCart: string;
  buttonAddToNextOrder: string;
  buttonAddedToNextOrder: string;
  freePrice: string;
};

export type ShopProductType = 'subscription' | 'subscriptionDouble' | 'oneTime';

export type Price = {
  amount: string;
  currencyCode: string;
};

export type ImageType = {
  src: string;
  altText: string | null;
};

// https://shopify.dev/docs/api/admin-rest/2024-07/resources/metafield
export type Metafield = {
  id: string;
  key: string;
  namespace: string;
  value: string;
};

export type SellingPlanAllocation = {
  sellingPlan: {
    id: string;
    name: string;
  };
};

export type ShopProductVariant = {
  id: string;
  price: Price;
  title: string;
  sku: string;
  currentlyNotInStock: boolean;
  availableForSale: boolean;
  quantityAvailable: number;
  image: ImageType;
  sellingPlanAllocations: Connection<SellingPlanAllocation>;
};

export type AlternateFlavorConfig = {
  originalVariantId?: string;
  color: string;
  hoverColor?: string;
  fontColor?: string;
  title: string;
  id: string;
  originalPrice?: number;
  singlePrice?: number;
  multiplePrice?: number;
  isComingSoon?: boolean;
  outOfStock?: boolean;
  flavorMessage?: Array<SanityKeyed<SanityBlock>>;
};

export type AlternateFlavor = AlternateFlavorConfig & {
  variant: CollectionProduct | ShopV2Product;
};

export type ShopV2Product = {
  id: string;
  description: string;
  descriptionHtml: string;
  title: string;
  featuredImage: ImageType;
  images: ImageType[];
  variants: ShopProductVariant[];
  tags: string[];
  metafields: Metafield[];
};

export type ShopProducts = {
  [ShopFilter.AllProducts]: ShopV2Product[];
  [ShopFilter.Subscriptions]: ShopV2Product[];
  [ShopFilter.Gifts]: ShopV2Product[];
  [ShopFilter.Supplements]: ShopV2Product[];
  [ShopFilter.Accessories]: ShopV2Product[];
  [ShopFilter.Gifts]: ShopV2Product[];
  [ShopFilter.Gear]: ShopV2Product[];
};

export type AddToSubscriptionModalHandler = (product: ShopV2Product) => void;

export type AddItemToCartHandler = (product: ShopV2Product) => void;

export type GetAddItemToCartHandler = (sectionType: ShopFilter) => AddItemToCartHandler;

export type AddItemToNextOrderHandler = (product: ShopV2Product) => void;

export interface AddToCartArgs {
  product: ShopProductVariant;
  forceNewCart?: boolean;
  shouldGiftCheckoutBeEnabled?: boolean;
  hasDoubleQuantity?: boolean;
  isSubscription?: boolean;
  isGiftItem?: boolean;
  sectionType: ShopFilter;
}

export type AlertModalType = 'sub_to_gift' | 'gift_to_sub' | 'otp_to_gift' | 'gift_to_otp';

export interface AlertModalState {
  modalType: AlertModalType | undefined;
  handleAgree: () => void;
}

// https://shopify.dev/docs/api/admin-rest/2024-07/resources/metafield
export type MetaField = {
  id: string;
  key: string;
  namespace: string;
  value: string;
};
